import { motion } from "framer-motion";
import { ArrowLeft, Send, Eye, Clock, Save, RotateCcw } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { EMAIL_STATUS_CONFIG, EMAIL_CATEGORY_CONFIG, EMAIL_PRIORITY_CONFIG } from "@/types/email";
import type { EmailTemplateConfig, EmailStatus, EmailCategory, EmailPriority } from "@/types/email";

const COOLDOWN_PRESETS = [
  { label: "Sem limite", value: 0 },
  { label: "5 min", value: 5 },
  { label: "1 hora", value: 60 },
  { label: "24 horas", value: 1440 },
  { label: "7 dias", value: 10080 },
  { label: "30 dias", value: 43200 },
];

export default function AdminEmailTemplateDetail() {
  const { key } = useParams<{ key: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [subject, setSubject] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [originalSubject, setOriginalSubject] = useState("");
  const [originalCooldown, setOriginalCooldown] = useState(0);

  const { data: template, isLoading } = useQuery({
    queryKey: ["email-template", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates_config")
        .select("*")
        .eq("template_key", key)
        .single();
      if (error) throw error;
      return data as EmailTemplateConfig;
    },
    enabled: !!key,
  });

  // Recent logs for this template
  const { data: recentLogs } = useQuery({
    queryKey: ["email-template-logs", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_logs")
        .select("id, recipient_email, recipient_name, status, created_at")
        .eq("template_key", key)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
    enabled: !!key,
  });

  // Sync local state with fetched data
  useEffect(() => {
    if (template) {
      setSubject(template.default_subject_template || "");
      setCooldown(template.cooldown_minutes);
      setOriginalSubject(template.default_subject_template || "");
      setOriginalCooldown(template.cooldown_minutes);
    }
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("email_templates_config")
        .update({
          default_subject_template: subject,
          cooldown_minutes: cooldown,
          updated_at: new Date().toISOString(),
        })
        .eq("template_key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Configuração salva" });
      setOriginalSubject(subject);
      setOriginalCooldown(cooldown);
      queryClient.invalidateQueries({ queryKey: ["email-template", key] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (active: boolean) => {
      const { error } = await supabase
        .from("email_templates_config")
        .update({
          is_active: active,
          last_toggled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("template_key", key);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-template", key] });
      toast({ title: "Status atualizado" });
    },
  });

  // Toggler admin name
  const { data: togglerName } = useQuery({
    queryKey: ["admin-profile", template?.last_toggled_by],
    queryFn: async () => {
      if (!template?.last_toggled_by) return null;
      const { data } = await supabase.from("profiles").select("full_name").eq("id", template.last_toggled_by).single();
      return data?.full_name || "Admin";
    },
    enabled: !!template?.last_toggled_by,
  });

  const subjectInputRef = useRef<HTMLInputElement>(null);
  const hasChanges = subject !== originalSubject || cooldown !== originalCooldown;
  const variableKeys = template?.variables_schema ? Object.keys(template.variables_schema) : [];

  // Subject preview with interpolated variables
  const subjectPreview = subject.replace(/\{(\w+)\}/g, (_, key) => {
    const val = template?.preview_defaults?.[key];
    return val != null ? String(val) : `{${key}}`;
  });

  if (isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl">
        <EmailTabNav />
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-muted rounded" />
          <div className="h-4 w-96 bg-muted rounded" />
          <div className="h-64 bg-muted rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl">
        <EmailTabNav />
        <p className="text-muted-foreground">Template não encontrado.</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      {/* Breadcrumb */}
      <Link to="/admin/emails/templates" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Templates
      </Link>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{template.display_name}</h1>
        <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
        <div className="flex flex-wrap gap-2">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${EMAIL_PRIORITY_CONFIG[template.priority as EmailPriority]?.color}`}>
            {EMAIL_PRIORITY_CONFIG[template.priority as EmailPriority]?.label}
          </span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${EMAIL_CATEGORY_CONFIG[template.category as EmailCategory]?.color}`}>
            {EMAIL_CATEGORY_CONFIG[template.category as EmailCategory]?.label}
          </span>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {template.recipient_type === "both" ? "Aluno + Empresa" : template.recipient_type === "student" ? "Aluno" : "Empresa"}
          </span>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {template.trigger_type}
          </span>
          <span className={`w-2 h-2 rounded-full mt-1 ${template.is_active ? "bg-green-500" : "bg-gray-300"}`} />
        </div>
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column (60%) */}
        <div className="lg:col-span-3 space-y-6">
          {/* Card 1: Controls */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-5 border border-border space-y-5">
            {/* Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Status do Template</p>
                <p className="text-xs text-muted-foreground">{template.is_active ? "Ativo — emails sendo enviados" : "Inativo — emails bloqueados"}</p>
              </div>
              <Switch
                checked={template.is_active}
                disabled={!template.is_implemented}
                onCheckedChange={(v) => toggleMutation.mutate(v)}
              />
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Assunto do Email</label>
              <Input
                ref={subjectInputRef}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Ex: Bem-vindo, {studentName}!"
              />
              {variableKeys.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {variableKeys.map((v) => (
                    <button
                      key={v}
                      onClick={() => {
                        const input = subjectInputRef.current;
                        if (input) {
                          const pos = input.selectionStart ?? subject.length;
                          const token = `{${v}}`;
                          setSubject(subject.slice(0, pos) + token + subject.slice(pos));
                          setTimeout(() => { input.focus(); input.setSelectionRange(pos + token.length, pos + token.length); }, 0);
                        } else {
                          setSubject((prev) => `${prev}{${v}}`);
                        }
                      }}
                      className="bg-muted rounded-full px-2.5 py-1 text-xs hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      {`{${v}}`}
                    </button>
                  ))}
                </div>
              )}
              {/* Subject preview */}
              {subject && (
                <p className="text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg">
                  <span className="font-medium">Preview:</span> {subjectPreview}
                </p>
              )}
            </div>

            {/* Cooldown */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Cooldown (minutos)</label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={cooldown}
                  onChange={(e) => setCooldown(Number(e.target.value))}
                  className="w-32"
                  min={0}
                />
                <div className="flex flex-wrap gap-1.5">
                  {COOLDOWN_PRESETS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setCooldown(p.value)}
                      className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                        cooldown === p.value
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Intervalo mínimo entre envios do mesmo tipo para o mesmo destinatário. 0 = sem limite.</p>
            </div>

            {/* Save */}
            {/* Subject validation (EM08 AC7) */}
            {subject === "" && (
              <p className="text-xs text-red-600">O assunto não pode ficar vazio.</p>
            )}
            {subject && (() => {
              const usedVars = subject.match(/\{(\w+)\}/g)?.map((v) => v.slice(1, -1)) || [];
              const invalidVars = usedVars.filter((v) => !variableKeys.includes(v));
              return invalidVars.length > 0 ? (
                <p className="text-xs text-red-600">
                  Variáveis inválidas: {invalidVars.map((v) => `{${v}}`).join(", ")}
                </p>
              ) : null;
            })()}

            <div className="flex gap-2 pt-2">
              <Button onClick={() => saveMutation.mutate()} disabled={!hasChanges || saveMutation.isPending || !subject}>
                <Save className="w-4 h-4 mr-2" /> Salvar
              </Button>
              <Button
                variant="ghost"
                disabled={!hasChanges}
                onClick={() => { setSubject(originalSubject); setCooldown(originalCooldown); }}
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Restaurar
              </Button>
            </div>
          </motion.div>

          {/* Card 2: Variables Schema */}
          {template.variables_schema && Object.keys(template.variables_schema).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl p-5 border border-border">
              <h3 className="text-base font-semibold mb-4">Variáveis do Template</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="text-xs font-medium text-muted-foreground uppercase border-b border-border">
                    <tr>
                      <th className="pb-2 pr-4">Nome</th>
                      <th className="pb-2 pr-4">Tipo</th>
                      <th className="pb-2 pr-4">Obrigatório</th>
                      <th className="pb-2">Label</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {Object.entries(template.variables_schema).map(([name, schema]) => (
                      <tr key={name}>
                        <td className="py-2.5 pr-4">
                          <code className="text-primary text-sm bg-primary/5 px-2 py-0.5 rounded">{name}</code>
                        </td>
                        <td className="py-2.5 pr-4 text-sm">{schema.type}</td>
                        <td className="py-2.5 pr-4 text-sm">{schema.required ? "Sim" : "Não"}</td>
                        <td className="py-2.5 text-sm text-muted-foreground">{schema.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Card 3: Audit */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-card rounded-2xl p-5 border border-border">
            <h3 className="text-base font-semibold mb-2">Última alteração</h3>
            <p className="text-sm text-muted-foreground">
              {template.last_toggled_at
                ? `${template.is_active ? "Ativado" : "Desativado"} por ${togglerName || "admin"} em ${new Date(template.last_toggled_at).toLocaleString("pt-BR")}`
                : "Nenhuma alteração registrada"}
            </p>
          </motion.div>
        </div>

        {/* Right column (40%) */}
        <div className="lg:col-span-2 space-y-6">
          {/* KPIs */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="space-y-3">
            {[
              { label: "Enviados", value: template.total_sent.toLocaleString("pt-BR"), color: "" },
              { label: "Falhas", value: String(template.total_failed), color: template.total_failed > 0 ? "text-red-600" : "" },
              { label: "Taxa Abertura", value: template.avg_open_rate != null ? `${template.avg_open_rate}%` : "—", color: "text-emerald-600" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-card rounded-2xl p-4 border border-border">
                <p className="text-xs text-muted-foreground uppercase">{kpi.label}</p>
                <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            ))}
            {template.last_sent_at && (
              <div className="bg-card rounded-2xl p-4 border border-border">
                <p className="text-xs text-muted-foreground uppercase">Último envio</p>
                <p className="text-sm font-medium">
                  {new Date(template.last_sent_at).toLocaleString("pt-BR")}
                </p>
              </div>
            )}
          </motion.div>

          {/* Preview quick */}
          {template.is_implemented && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="text-sm font-medium">Preview</span>
                <Link to={`/admin/emails/templates/${key}/preview`} className="text-xs text-primary hover:underline">
                  Tela cheia →
                </Link>
              </div>
              <div className="p-4 flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/admin/emails/templates/${key}/preview`}>
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/admin/emails/templates/${key}/preview`}>
                    <Send className="w-4 h-4 mr-1" /> Enviar teste
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/admin/emails/send/${key}`}>
                    <Send className="w-4 h-4 mr-1" /> Disparar
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}

          {/* Recent logs */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-card rounded-2xl p-5 border border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Últimos 10 Envios</h3>
              <Link to={`/admin/emails/log?template=${key}`} className="text-xs text-primary hover:underline">
                Ver todos →
              </Link>
            </div>
            {recentLogs && recentLogs.length > 0 ? (
              <div className="space-y-2">
                {recentLogs.map((log) => {
                  const sc = EMAIL_STATUS_CONFIG[log.status as EmailStatus];
                  return (
                    <div key={log.id} className="flex items-center justify-between text-sm">
                      <div className="truncate mr-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(log.created_at).toLocaleDateString("pt-BR")}
                        </span>{" "}
                        <span className="font-medium">{log.recipient_email}</span>
                      </div>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${sc?.color}`}>
                        {sc?.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Nenhum envio registrado</p>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
